// Base UI's `.mts` subpath exports trip the strict `import/extensions` rule
// (same reason the migrated `ui/` primitives are exempted in eslint.config.js).
// This is a hand-written component so it stays fully linted otherwise —
// disable only the extension rule on the two Base UI radio imports.
// eslint-disable-next-line import/extensions
import { Radio } from "@base-ui/react/radio"
// eslint-disable-next-line import/extensions
import { RadioGroup } from "@base-ui/react/radio-group"
import { type VariantProps } from "class-variance-authority"
import type * as React from "react"

import { toggleVariants } from "@/components/ui/toggle"
import { cn } from "@/lib/utils"

/**
 * OKS-625 — Segmented control built on Base UI radio-group.
 *
 * Replaces the Radix `ToggleGroup type="single"` previously used by the
 * analytics window/range controls. A single-select segmented control is
 * semantically a radio group, so this preserves the accessibility contract
 * that `ToggleGroup type="single"` gave us for free:
 *   - each option is `role="radio"` with `aria-checked`,
 *   - exactly one option is always selected (radio-group enforces this —
 *     re-selecting the active option is a native no-op; there is no
 *     deselect-to-empty),
 *   - ←/→ and ↑/↓ arrow keys rove between options (roving tabindex).
 *
 * Visual parity with the old segmented control is achieved by reusing the
 * exported `toggleVariants` cva for structure (padding / radius / border-
 * collapse / focus-ring / sizing). The active-pill fill is driven off the
 * radio's real checked attribute (`data-checked:`) — NOT the toggle cva's
 * `data-pressed:` selector, which is inert on a radio element.
 *
 * The interactive target is the visible `[role="radio"]` element (Base UI
 * renders a hidden native input beside it); `data-testid` + `aria-label`
 * land on that visible element so system/E2E clicks hit the pill (OKS-614).
 */
export interface SegmentedControlOption<V extends string> {
  /** The value emitted via `onValueChange` when this option is selected. */
  value: V
  /** Visible pill content (typically a translated label). */
  label: React.ReactNode
  /** Preserved verbatim on the visible pill so specs/page wiring hold. */
  "data-testid"?: string
  /** Per-option accessible name (falls back to the visible label). */
  "aria-label"?: string
}

export interface SegmentedControlProps<V extends string> extends VariantProps<
  typeof toggleVariants
> {
  /** Controlled selected value (URL-echoed by callers). */
  value: V
  /** Fired with the newly-selected value. Never fires for the active option. */
  onValueChange: (value: V) => void
  options: SegmentedControlOption<V>[]
  className?: string
  /** Accessible name for the radiogroup container. */
  "aria-label"?: string
  /** Preserved verbatim on the radiogroup container. */
  "data-testid"?: string
}

export function SegmentedControl<V extends string>({
  value,
  onValueChange,
  options,
  variant = "default",
  size = "default",
  className,
  ...props
}: SegmentedControlProps<V>) {
  return (
    <RadioGroup
      value={value}
      onValueChange={(next) => onValueChange(next)}
      data-slot="segmented-control"
      data-variant={variant}
      data-size={size}
      className={cn("flex w-fit items-center rounded-md", className)}
      {...props}
    >
      {options.map((option) => (
        <Radio.Root
          key={option.value}
          value={option.value}
          data-slot="segmented-control-item"
          data-variant={variant}
          data-size={size}
          data-testid={option["data-testid"]}
          aria-label={option["aria-label"]}
          className={cn(
            toggleVariants({ variant, size }),
            "w-auto min-w-0 shrink-0 px-3 focus:z-10 focus-visible:z-10",
            "rounded-none shadow-none first:rounded-l-md last:rounded-r-md",
            "data-[variant=outline]:border-l-0 data-[variant=outline]:first:border-l",
            "data-checked:bg-accent data-checked:text-accent-foreground",
          )}
        >
          {option.label}
        </Radio.Root>
      ))}
    </RadioGroup>
  )
}
